import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Alert, CircularProgress, Chip,
  Divider, IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon   from '@mui/icons-material/ArrowBack';
import SaveIcon        from '@mui/icons-material/Save';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PrintIcon       from '@mui/icons-material/Print';
import ScienceIcon     from '@mui/icons-material/Science';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { phosphateService } from '../services/api';
import { PhosphateDailyReport } from '../types';

// ── constants ────────────────────────────────────────────────────────────────
const FERMENTERS = ['F1', 'F2', 'F3', 'IST', 'MIXING PIT'] as const;
type Fermenter = typeof FERMENTERS[number];

/** mg/L = ppm × 200 */
const calcMgL = (ppm: number | null): number | null =>
  ppm == null ? null : +(ppm * 200).toFixed(4);

const calcReactiveRemark = (mgL: number | null) => {
  if (mgL == null) return null;
  if (mgL < 10)   return 'Below range';
  if (mgL <= 200) return 'Beneficial';
  return 'Above range';
};

const calcTotalRemark = (mgL: number | null) => {
  if (mgL == null) return null;
  if (mgL < 100)    return 'Below range';
  if (mgL <= 1000)  return 'Beneficial';
  return 'Above range';
};

const remarkColor = (r: string | null) => {
  if (r === 'Beneficial')  return 'success';
  if (r === 'Below range') return 'info';
  if (r === 'Above range') return 'error';
  return 'default';
};

const toN = (s: string) => { const n = parseFloat(s); return isNaN(n) ? null : n; };

type FermRow = { reactivePpm: string; totalPpm: string };
const emptyRows = (): Record<Fermenter, FermRow> =>
  Object.fromEntries(FERMENTERS.map((f) => [f, { reactivePpm: '', totalPpm: '' }])) as Record<Fermenter, FermRow>;

// ── component ─────────────────────────────────────────────────────────────────
const PhosphateForm: React.FC = () => {
  const today        = new Date().toISOString().split('T')[0];
  const navigate     = useNavigate();
  const [searchParams] = useSearchParams();

  /** 'reactive' | 'total' | 'both'  — driven by ?mode= query param */
  const mode = (searchParams.get('mode') as 'reactive' | 'total' | null) ?? 'both';
  const showReactive = mode === 'reactive' || mode === 'both';
  const showTotal    = mode === 'total'    || mode === 'both';

  const headerGradient =
    mode === 'reactive' ? 'linear-gradient(135deg, #880e4f 0%, #ad1457 60%, #c2185b 100%)' :
    mode === 'total'    ? 'linear-gradient(135deg, #bf360c 0%, #e64a19 60%, #f57c00 100%)' :
    'linear-gradient(135deg, #880e4f 0%, #ad1457 40%, #e65100 100%)';
  const headerTitle =
    mode === 'reactive' ? 'Phosphate — Reactive (PO₄³⁻-P)' :
    mode === 'total'    ? 'Phosphate — Total (PO₄³⁻)' :
    'Phosphate — Reactive & Total';
  const primaryColor = mode === 'total' ? '#e65100' : '#880e4f';
  const saveColor    = mode === 'total'
    ? { bgcolor: '#bf360c', '&:hover': { bgcolor: '#e64a19' } }
    : { bgcolor: '#880e4f', '&:hover': { bgcolor: '#ad1457' } };

  const [date,         setDate]         = useState(today);
  const [rows,         setRows]         = useState<Record<Fermenter, FermRow>>(emptyRows());
  const [saving,       setSaving]       = useState(false);
  const [loadingDates, setLoadingDates] = useState(false);
  const [dates,        setDates]        = useState<string[]>([]);
  const [savedReport,  setSavedReport]  = useState<PhosphateDailyReport | null>(null);
  const [error,        setError]        = useState<string | null>(null);
  const [success,      setSuccess]      = useState<string | null>(null);

  // ── computed live preview ────────────────────────────────────────────────
  const computed = FERMENTERS.map((ferm) => {
    const reactivePpm  = toN(rows[ferm].reactivePpm);
    const reactiveMgL  = calcMgL(reactivePpm);
    const totalPpm     = toN(rows[ferm].totalPpm);
    const totalMgL     = calcMgL(totalPpm);
    return {
      ferm,
      reactivePpm, reactiveMgL, reactiveRemark: calcReactiveRemark(reactiveMgL),
      totalPpm,    totalMgL,    totalRemark:    calcTotalRemark(totalMgL),
    };
  });

  // ── fetch available dates ────────────────────────────────────────────────
  const fetchDates = useCallback(async () => {
    setLoadingDates(true);
    try {
      const d = await phosphateService.getAvailableDates();
      setDates(d);
    } catch { setDates([]); }
    finally { setLoadingDates(false); }
  }, []);

  useEffect(() => { fetchDates(); }, [fetchDates]);

  // ── load existing data when date changes ─────────────────────────────────
  const loadDate = useCallback(async (d: string) => {
    if (!d) return;
    try {
      const report: PhosphateDailyReport = await phosphateService.getDailyReport(d);
      setSavedReport(report);
      const newRows = emptyRows();
      for (const fe of report.fermenters) {
        if (FERMENTERS.includes(fe.fermenter as Fermenter)) {
          const f = fe.fermenter as Fermenter;
          newRows[f] = {
            reactivePpm: fe.reactivePpm != null ? String(fe.reactivePpm) : '',
            totalPpm:    fe.totalPpm    != null ? String(fe.totalPpm)    : '',
          };
        }
      }
      setRows(newRows);
    } catch {
      setSavedReport(null);
      setRows(emptyRows());
    }
  }, []);

  useEffect(() => { loadDate(date); }, [date, loadDate]);

  // ── save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // Only include the field(s) relevant to the current mode so the backend
      // preserves the other side's existing data (partial update).
      const entries = FERMENTERS
        .filter((f) => {
          if (showReactive && !showTotal) return rows[f].reactivePpm.trim() !== '';
          if (showTotal && !showReactive) return rows[f].totalPpm.trim() !== '';
          return rows[f].reactivePpm.trim() !== '' || rows[f].totalPpm.trim() !== '';
        })
        .map((f) => {
          const base: any = { measurementDate: date, fermenter: f };
          if (showReactive) base.reactivePpm = toN(rows[f].reactivePpm) ?? undefined;
          if (showTotal)    base.totalPpm    = toN(rows[f].totalPpm)    ?? undefined;
          return base;
        });

      if (entries.length === 0) {
        setError(`Enter at least one ${mode === 'reactive' ? 'Reactive' : mode === 'total' ? 'Total' : 'Reactive or Total'} ppm value.`);
        setSaving(false);
        return;
      }
      await phosphateService.upsertBatch(entries);
      setSuccess(`Saved ${entries.length} entry(ies) for ${date}.`);
      fetchDates();
      loadDate(date);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── delete ───────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!window.confirm(`Delete all Phosphate entries for ${date}?`)) return;
    try {
      await phosphateService.deleteByDate(date);
      setRows(emptyRows());
      setSavedReport(null);
      setSuccess(`Deleted Phosphate entries for ${date}.`);
      fetchDates();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Delete failed');
    }
  };

  // ── helpers for column header style ─────────────────────────────────────
  const TH = (bg = '#880e4f') => ({
    color: '#fff', fontWeight: 700, fontSize: '0.72rem',
    border: '1px solid rgba(255,255,255,0.25)', bgcolor: bg,
    whiteSpace: 'nowrap' as const, px: 1.5, py: 1,
  });
  const TD = { border: '1px solid #e0e0e0', fontSize: '0.74rem', px: 1.5, py: 0.6 };

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1300, mx: 'auto' }}>

      {/* Header */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        background: headerGradient,
        color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5,
        justifyContent: 'space-between', flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/lab-report')} sx={{ color: '#fff', mr: 0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <ScienceIcon sx={{ fontSize: '2rem', opacity: 0.9 }} />
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.8, lineHeight: 1, letterSpacing: 2, display: 'block' }}>
              POLOMOLOK — CHEMICAL LAB
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {headerTitle}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Print">
          <IconButton onClick={() => window.print()} sx={{ color: '#fff' }}>
            <PrintIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Date picker */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Measurement Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            size="small"
            inputProps={{ max: today }}
            sx={{ minWidth: 180 }}
          />
          {loadingDates && <CircularProgress size={18} />}
          {dates.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {dates.slice(0, 12).map((d) => (
                <Chip
                  key={d}
                  label={d}
                  size="small"
                  variant={d === date ? 'filled' : 'outlined'}
                  color={d === date ? 'secondary' : 'default'}
                  onClick={() => setDate(d)}
                  sx={{ cursor: 'pointer', fontSize: '0.72rem' }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Feedback */}
      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Input table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: primaryColor }}>Data Entry</Typography>
          <Typography variant="caption" sx={{ color: '#888' }}>mg/L = ppm × 200</Typography>
        </Box>
        <Divider />
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: showReactive && showTotal ? 900 : 500 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ ...TH(), minWidth: 130 }}>Fermenter</TableCell>
                  {showReactive && (
                    <TableCell align="center" sx={{ ...TH('#ad1457'), minWidth: 155 }}>ppm PO₄³⁻-P (input)</TableCell>
                  )}
                  {showReactive && (
                    <TableCell align="center" sx={{ ...TH('#ad1457'), minWidth: 130 }}>mg/L (calc)</TableCell>
                  )}
                  {showReactive && (
                    <TableCell align="center" sx={{ ...TH('#c2185b'), minWidth: 130 }}>Remark · Reactive</TableCell>
                  )}
                  {showTotal && (
                    <TableCell align="center" sx={{ ...TH('#f57c00'), minWidth: 155 }}>ppm PO₄³⁻ (input)</TableCell>
                  )}
                  {showTotal && (
                    <TableCell align="center" sx={{ ...TH('#f57c00'), minWidth: 130 }}>mg/L (calc)</TableCell>
                  )}
                  {showTotal && (
                    <TableCell align="center" sx={{ ...TH('#e65100'), minWidth: 130 }}>Remark · Total</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {FERMENTERS.map((ferm, idx) => {
                  const c = computed[idx];
                  const rowBg = mode === 'total' ? '#fff3e0' : '#fce4ec';
                  return (
                    <TableRow key={ferm} hover>
                      <TableCell align="center" sx={{ ...TD, fontWeight: 700, bgcolor: rowBg }}>
                        {ferm}
                      </TableCell>
                      {showReactive && (
                        <TableCell align="center" sx={{ ...TD, p: 0.6 }}>
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ step: '0.01', min: 0 }}
                            value={rows[ferm].reactivePpm}
                            onChange={(e) => setRows((prev) => ({
                              ...prev, [ferm]: { ...prev[ferm], reactivePpm: e.target.value },
                            }))}
                            placeholder="e.g. 0.76"
                            sx={{ width: 130 }}
                          />
                        </TableCell>
                      )}
                      {showReactive && (
                        <TableCell align="center" sx={{
                          ...TD, fontWeight: c.reactiveMgL != null ? 600 : 400,
                          bgcolor: c.reactiveMgL != null ? '#fce4ec' : undefined, color: '#ad1457',
                        }}>
                          {c.reactiveMgL != null ? c.reactiveMgL.toFixed(0) : '—'}
                        </TableCell>
                      )}
                      {showReactive && (
                        <TableCell align="center" sx={TD}>
                          {c.reactiveRemark
                            ? <Chip label={c.reactiveRemark} size="small" color={remarkColor(c.reactiveRemark) as any} variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                            : '—'}
                        </TableCell>
                      )}
                      {showTotal && (
                        <TableCell align="center" sx={{ ...TD, p: 0.6 }}>
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ step: '0.01', min: 0 }}
                            value={rows[ferm].totalPpm}
                            onChange={(e) => setRows((prev) => ({
                              ...prev, [ferm]: { ...prev[ferm], totalPpm: e.target.value },
                            }))}
                            placeholder="e.g. 0.91"
                            sx={{ width: 130 }}
                          />
                        </TableCell>
                      )}
                      {showTotal && (
                        <TableCell align="center" sx={{
                          ...TD, fontWeight: c.totalMgL != null ? 600 : 400,
                          bgcolor: c.totalMgL != null ? '#fff3e0' : undefined, color: '#e65100',
                        }}>
                          {c.totalMgL != null ? c.totalMgL.toFixed(0) : '—'}
                        </TableCell>
                      )}
                      {showTotal && (
                        <TableCell align="center" sx={TD}>
                          {c.totalRemark
                            ? <Chip label={c.totalRemark} size="small" color={remarkColor(c.totalRemark) as any} variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                            : '—'}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Action buttons */}
        <Box sx={{ p: 2, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={handleDelete}
            disabled={saving || !savedReport}
          >
            Delete Date
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={saveColor}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Paper>

      {/* Saved report preview */}
      {savedReport && savedReport.fermenters.some((f) =>
        (showReactive && f.reactivePpm != null) || (showTotal && f.totalPpm != null)
      ) && (
        <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: primaryColor }}>
              Saved Report — {savedReport.measurementDate}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              {showReactive && 'Reactive: 10–200 mg/L Beneficial'}
              {showReactive && showTotal && ' · '}
              {showTotal    && 'Total: 100–1000 mg/L Beneficial'}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ overflowX: 'auto' }}>
            <TableContainer>
              <Table size="small" sx={{ minWidth: showReactive && showTotal ? 900 : 500 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ ...TH(), minWidth: 130 }}>Fermenter</TableCell>
                    {showReactive && <TableCell align="center" sx={{ ...TH('#ad1457') }}>ppm PO₄³⁻-P</TableCell>}
                    {showReactive && <TableCell align="center" sx={{ ...TH('#ad1457') }}>mg/L (calc)</TableCell>}
                    {showReactive && <TableCell align="center" sx={{ ...TH('#c2185b') }}>Target Range</TableCell>}
                    {showReactive && <TableCell align="center" sx={{ ...TH('#c2185b') }}>Remarks</TableCell>}
                    {showTotal    && <TableCell align="center" sx={{ ...TH('#f57c00') }}>ppm PO₄³⁻</TableCell>}
                    {showTotal    && <TableCell align="center" sx={{ ...TH('#f57c00') }}>mg/L (calc)</TableCell>}
                    {showTotal    && <TableCell align="center" sx={{ ...TH('#e65100') }}>Target Range</TableCell>}
                    {showTotal    && <TableCell align="center" sx={{ ...TH('#e65100') }}>Remarks</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {savedReport.fermenters
                    .filter((f) => (showReactive && f.reactivePpm != null) || (showTotal && f.totalPpm != null))
                    .map((f) => (
                      <TableRow key={f.fermenter} hover>
                        <TableCell align="center" sx={{ ...TD, fontWeight: 700, bgcolor: mode === 'total' ? '#fff3e0' : '#fce4ec' }}>{f.fermenter}</TableCell>
                        {showReactive && <TableCell align="center" sx={TD}>{f.reactivePpm != null ? Number(f.reactivePpm).toFixed(2) : '—'}</TableCell>}
                        {showReactive && <TableCell align="center" sx={{ ...TD, fontWeight: 600, color: '#ad1457', bgcolor: '#fce4ec' }}>{f.reactiveMgL != null ? Number(f.reactiveMgL).toFixed(0) : '—'}</TableCell>}
                        {showReactive && <TableCell align="center" sx={{ ...TD, fontSize: '0.7rem', color: '#546e7a' }}>PO₄³⁻-P / 10-200 mg/L</TableCell>}
                        {showReactive && <TableCell align="center" sx={TD}>{f.reactiveRemark ? <Chip label={f.reactiveRemark} size="small" color={remarkColor(f.reactiveRemark) as any} variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} /> : '—'}</TableCell>}
                        {showTotal    && <TableCell align="center" sx={TD}>{f.totalPpm != null ? Number(f.totalPpm).toFixed(2) : '—'}</TableCell>}
                        {showTotal    && <TableCell align="center" sx={{ ...TD, fontWeight: 600, color: '#e65100', bgcolor: '#fff3e0' }}>{f.totalMgL != null ? Number(f.totalMgL).toFixed(0) : '—'}</TableCell>}
                        {showTotal    && <TableCell align="center" sx={{ ...TD, fontSize: '0.7rem', color: '#546e7a' }}>Total / 100-1000 mg/L</TableCell>}
                        {showTotal    && <TableCell align="center" sx={TD}>{f.totalRemark ? <Chip label={f.totalRemark} size="small" color={remarkColor(f.totalRemark) as any} variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} /> : '—'}</TableCell>}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      )}

      {/* Legend */}
      <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fce4ec', borderRadius: 2, border: '1px solid #f48fb1' }}>
        <Typography variant="caption" sx={{ color: '#880e4f', display: 'block', fontWeight: 700, mb: 0.5 }}>Interpretation</Typography>
        <Typography variant="caption" sx={{ color: '#1b5e20', display: 'block' }}>
          <strong>Beneficial:</strong> "Supports healthy microbial activity and stable biogas production."
        </Typography>
        <Typography variant="caption" sx={{ color: '#c62828', display: 'block' }}>
          <strong>Above range:</strong> "Can precipitate with magnesium and ammonium, causing scaling in pipes and reactors."
        </Typography>
        <Typography variant="caption" sx={{ color: '#1565c0', display: 'block' }}>
          <strong>Below range:</strong> "Can limit microbial growth, leading to reduced methane yield."
        </Typography>
      </Box>
    </Box>
  );
};

export default PhosphateForm;
